import { useState, useCallback, useRef } from 'react'

/**
 * useHistory - Undo/Redo機能を提供するカスタムフック
 * @param {any} initialState - 初期状態
 * @param {number} maxHistory - 保持する履歴の最大数（デフォルト: 50）
 * @returns {object} - state, setState, undo, redo, canUndo, canRedo
 */
export function useHistory(initialState, maxHistory = 50) {
    // 現在の状態
    const [state, setStateInternal] = useState(initialState)

    // 履歴スタック（過去の状態）
    const historyRef = useRef([])

    // 未来スタック（Undoした状態）
    const futureRef = useRef([])

    // 状態を更新（履歴に追加）
    const setState = useCallback((newState, overwrite = false) => {
        // 関数として渡された場合は現在の状態を使って計算
        const resolvedState = typeof newState === 'function' ? newState(state) : newState

        // 上書きモードでない場合のみ履歴に追加
        if (!overwrite) {
            // 現在の状態を履歴に追加
            historyRef.current = [...historyRef.current, state].slice(-maxHistory)

            // 新しい変更があったのでfutureをクリア
            futureRef.current = []
        }

        // 状態を更新
        setStateInternal(resolvedState)
    }, [state, maxHistory])

    // Undo - 1つ前の状態に戻す
    const undo = useCallback(() => {
        if (historyRef.current.length === 0) return

        // 履歴から最後の状態を取り出す
        const previousState = historyRef.current[historyRef.current.length - 1]
        historyRef.current = historyRef.current.slice(0, -1)

        // 現在の状態をfutureに追加
        futureRef.current = [...futureRef.current, state]

        // 状態を復元
        setStateInternal(previousState)
    }, [state])

    // Redo - Undoを取り消す
    const redo = useCallback(() => {
        if (futureRef.current.length === 0) return

        // futureから最後の状態を取り出す
        const nextState = futureRef.current[futureRef.current.length - 1]
        futureRef.current = futureRef.current.slice(0, -1)

        // 現在の状態を履歴に追加
        historyRef.current = [...historyRef.current, state]

        // 状態を復元
        setStateInternal(nextState)
    }, [state])

    // Undo可能かどうか
    const canUndo = historyRef.current.length > 0

    // Redo可能かどうか
    const canRedo = futureRef.current.length > 0

    return {
        state,
        setState,
        undo,
        redo,
        canUndo,
        canRedo,
    }
}
